import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useState, useMemo, useContext } from "react";
import { useForm, Controller } from "react-hook-form";
import companyFinepFormSchema from "../../../../Schemas/companyFinepSchema";
import TabsHeader from "../../../../Components/Shared/TabsHeader";
import { DatePickerControlledInput } from "../../../../Components/Shared/DatePickerControlledInput";
import { CompanyFinepModel } from "../../../../Models/CompanyFinepModel";
import { CreateCompanyFinep, EditCompanyFinep, HasOtherCompany, ValidateCompanyFinep } from "../../../../API/CompanyFinepAPI";
import { useNavigate, useParams } from "react-router-dom";
import { CompanyFinepContext } from "../../../../Contexts/CompanyFinepContext";
import { useToast } from "../../../../antd/hooks/toast";
import { useAuth } from "../../../../Hooks/useAuth";
import { Form, Input, Select, Button, Typography, Row, Col, Space, Spin } from 'antd';
import { PlusOutlined } from "@ant-design/icons";
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;
interface CompanyFinepTabProps {
    company?: CompanyFinepModel;
}

const CompanyFinepTab = ({ company }: CompanyFinepTabProps) => {
    const { control, handleSubmit, formState: { errors }, setValue } = useForm<CompanyFinepModel>({
        resolver: yupResolver(companyFinepFormSchema),
        defaultValues: useMemo(() => {
            if (company) {
                return {
                    ...company,
                    foundationDate: company.foundationDate ? dayjs(company.foundationDate) : null,
                    operationStartDate: company.operationStartDate ? dayjs(company.operationStartDate) : null,
                    revenueOpeningDate: company.revenueOpeningDate ? dayjs(company.revenueOpeningDate) : null
                };
            }
            return company;
        }, [company])
    });

    const toast = useToast();
    const [validateField, setValidateField] = useState(false);
    const [hasCommercialRegistration, setHasCommercialRegistration] = useState(company?.hasCommercialRegistration ? true : false);
    const [hasStateRegistration, setHasStateRegistration] = useState(company?.hasStateRegistration ? true : false);
    const [hasCivilRegistration, setHasCivilRegistration] = useState(company?.hasCivilRegistration ? true : false);
    const [isExecutor, setIsExecutor] = useState(company?.isExecutor?.toString() || "0");
    const [hasOtherCompany, setHasOtherCompany] = useState(false);
    const [loading, setLoading] = useState(false);
    const user = useAuth();
    const { addCompany, changeTab, handleFillTab, handleValidateTab, handleInvalidateTab } = useContext(CompanyFinepContext);
    const navigate = useNavigate();
    const { id, registerId, companyId } = useParams();

    async function onSubmit(data: CompanyFinepModel) {
        data.registerId = registerId;
        data.userEmail = user.userEmail;
        data.isContentValid = validateField;
        if (!hasOtherCompany) {
            data.isExecutor = 0;
        } else {
            data.isExecutor = isExecutor ? parseInt(isExecutor) : null;
        }

        if (data.foundationDate) {
            data.foundationDate = dayjs(data.foundationDate).toISOString();
        }
        if (data.operationStartDate) {
            data.operationStartDate = dayjs(data.operationStartDate).toISOString();
        }
        if (data.revenueOpeningDate) {
            data.revenueOpeningDate = dayjs(data.revenueOpeningDate).toISOString();
        }
        setLoading(true);
        try {
            if (company?.id) {
                data.id = company.id;
                const response = await EditCompanyFinep(data);
                if (response.statusCode === 200) {
                    toast.success({ message: 'Cadastro editado!' });
                    changeTab(1);
                }
            } else {
                handleFillTab(0);
                const response = await CreateCompanyFinep(data);
                if (response.statusCode === 200) {
                    addCompany(response.data);
                    navigate(`/companyFinep/${response.data.id}/${registerId}/${id}`);
                    toast.success({ message: 'Cadastro realizado!' });
                    changeTab(1);
                } else if (Array.isArray(response.error)) {
                    response.errors.forEach((error: string) => toast.error({ message: error }));
                }
            }
        } catch (error) {
            console.error('Error submitting form:', error);
            toast.error({ message: 'Ocorreu um erro ao enviar o formulário' });
        } finally {
            setLoading(false);
        }
    }

    async function handleValidate(isValid: boolean) {
        setLoading(true);
        setValidateField(isValid);
        try {
            await ValidateCompanyFinep({
                id: companyId ?? "",
                isContentValid: isValid,
                userEmail: user.userEmail
            });
            if (isValid) {
                handleValidateTab(3);
            } else {
                handleInvalidateTab(3);
            }
        } catch (error) {
            console.error('Error validating:', error);
        } finally {
            setLoading(false);
        }
    }

    async function getHasOtherCompany() {
        setLoading(true);
        try {
            const result = await HasOtherCompany(registerId ?? "");
            setHasOtherCompany(result);
        } catch (error) {
            console.error('Error checking other company:', error);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (company) {
            setHasCivilRegistration(company.hasCivilRegistration ? true : false);
            setHasCommercialRegistration(company.hasCommercialRegistration ? true : false);
            setHasStateRegistration(company.hasStateRegistration ? true : false);
            setValidateField(true);
        }
        getHasOtherCompany();
    }, [company]);

    const formatCnpj = (value: string) => {
        if (!value) return "";
        return value.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
    };

    const parseCnpj = (value: string) => {
        if (!value) return "";
        return value.replace(/\D/g, "");
    };
    
    return (
        <Spin spinning={loading}>
            <TabsHeader
                canValidate={true}
                valueSelected={validateField}
                changeValidate={handleValidate}
                registerId={registerId}
                cardName="Pessoa Jurídica"
                tabName="Cartão CNPJ"
            />
            <Form layout="vertical" onFinish={handleSubmit(onSubmit)}>
                <div className="mt-4">
                    <Title level={4} style={{ color: '#000085', fontSize: '22px', fontWeight: 'normal' }}>
                        Cadastro Pessoa Jurídica - Proponente ou Executora
                    </Title>
                    <Text style={{ color: '#777777', fontSize: '18px', fontWeight: 'normal' }}>
                        Informações primárias da empresa
                    </Text>
                    <Row align="middle" className="mt-4">
                        <Col span={24}>
                            <Form.Item label={<span style={{ fontWeight: 'normal' }}>Selecione o Tipo de Pessoa Jurídica:</span>}>
                                {hasOtherCompany ? (
                                    <Controller
                                        name="isExecutor"
                                        control={control}
                                        render={({ field }) => (
                                            <Select
                                                {...field}
                                                value={isExecutor}
                                                onChange={(value) => {
                                                    setIsExecutor(value);
                                                    setValue("isExecutor", parseInt(value));
                                                }}
                                                style={{ width: '100%' }}
                                            >
                                                <Option value="1">Executora</Option>
                                                <Option value="2">Co-Executora</Option>
                                            </Select>
                                        )}
                                    />
                                ) : (
                                    <Select value="0" style={{ width: '100%' }} disabled>
                                        <Option value="0">Proponente</Option>
                                    </Select>
                                )}
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16} className="mt-4">
                        <Col span={6}>
                            <Form.Item
                                label="CNPJ:"
                                required
                                validateStatus={errors.cnpj ? "error" : ""}
                                help={errors.cnpj?.message as React.ReactNode}
                            >
                                <Controller
                                    name="cnpj"
                                    control={control}
                                    rules={{ required: "CNPJ é obrigatório" }}
                                    render={({ field }) => (
                                        <Input
                                            {...field}
                                            placeholder="Digite o CNPJ"
                                            maxLength={18}
                                            onChange={(e) => {
                                                const rawValue = parseCnpj(e.target.value);
                                                field.onChange(rawValue);
                                            }}
                                            value={formatCnpj(field.value)}
                                        />
                                    )}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item
                                label="Razão Social:"
                                required
                                validateStatus={errors.legalName ? "error" : ""}
                                help={errors.legalName?.message as React.ReactNode}
                            >
                                <Controller
                                    name="legalName"
                                    control={control}
                                    rules={{ required: "Razão Social é obrigatória" }}
                                    render={({ field }) => (
                                        <Input {...field} placeholder="Digite a Razão Social" />
                                    )}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item
                                label="Nome Fantasia:"
                                required
                                validateStatus={errors.tradeName ? "error" : ""}
                                help={errors.tradeName?.message as React.ReactNode}
                            >
                                <Controller
                                    name="tradeName"
                                    control={control}
                                    rules={{ required: "Nome Fantasia é obrigatório" }}
                                    render={({ field }) => (
                                        <Input {...field} placeholder="Digite o Nome Fantasia" />
                                    )}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item
                                label="Sigla:"
                                required
                                validateStatus={errors.abbreviation ? "error" : ""}
                                help={errors.abbreviation?.message as React.ReactNode}
                            >
                                <Controller
                                    name="abbreviation"
                                    control={control}
                                    rules={{ required: "Sigla é obrigatória" }}
                                    render={({ field }) => (
                                        <Input {...field} placeholder="Digite a Sigla" />
                                    )}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16} className="mt-4">
                        <Col span={6}>
                            <Form.Item label="Natureza Jurídica:">
                                <Controller
                                    name="legalType"
                                    control={control}
                                    render={({ field }) => (
                                        <Input {...field} placeholder="Digite a Natureza" />
                                    )}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item label="CNAE:">
                                <Controller
                                    name="cnaeCode"
                                    control={control}
                                    render={({ field }) => (
                                        <Input {...field} placeholder="Digite o CNAE" />
                                    )}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item label="WebSite:">
                                <Controller
                                    name="website"
                                    control={control}
                                    render={({ field }) => (
                                        <Input {...field} placeholder="Digite o Site" />
                                    )}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item
                                label="Capital Social Atualizado:"
                                required
                                validateStatus={errors.updatedCapital ? "error" : ""}
                                help={errors.updatedCapital?.message as React.ReactNode}
                            >
                                <Controller
                                    name="updatedCapital"
                                    control={control}
                                    rules={{ required: "Capital Social Atualizado é obrigatório" }}
                                    render={({ field }) => (
                                        <Input {...field} placeholder="Digite o Capital Social Atualizado" />
                                    )}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16} className="mt-4">
                        <Col span={8}>
                            <DatePickerControlledInput
                                control={control}
                                inputName="foundationDate"
                                label="Data de Fundação:"
                            />
                        </Col>
                        <Col span={8}>
                            <DatePickerControlledInput
                                control={control}
                                inputName="operationStartDate"
                                label="Data de Início da Operação:"
                            />
                        </Col>
                        <Col span={8}>
                            <DatePickerControlledInput
                                control={control}
                                inputName="revenueOpeningDate"
                                label="Data de Abertura na Receita:"
                            />
                        </Col>
                    </Row>
                    <Row gutter={16} className="mt-4">
                        <Col span={8}>
                            <Form.Item label="Possui registro na Junta Comercial ou Registro Civíl de Pessoa Jurídica?">
                                <Space direction="vertical" style={{ width: '100%' }}>
                                    <Select
                                        value={hasCommercialRegistration ? "1" : "0"}
                                        onChange={(value) => {
                                            setHasCommercialRegistration(value === '1');
                                            if (value === '0') {
                                                setValue("hasCommercialRegistration", undefined);
                                            }
                                        }}
                                    >
                                        <Option value="1">Sim</Option>
                                        <Option value="0">Não</Option>
                                    </Select>
                                    {hasCommercialRegistration && (
                                        <Controller
                                            name="hasCommercialRegistration"
                                            control={control}
                                            render={({ field }) => (
                                                <Input {...field} type="number" placeholder="Número" />
                                            )}
                                        />
                                    )}
                                </Space>
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item label="Possui Inscrição Estadual?">
                                <Space direction="vertical" style={{ width: '100%' }}>
                                    <Select
                                        value={hasStateRegistration ? "1" : "0"}
                                        onChange={(value) => {
                                            setHasStateRegistration(value === '1');
                                            if (value === '0') {
                                                setValue("hasStateRegistration", undefined);
                                            }
                                        }}
                                    >
                                        <Option value="1">Sim</Option>
                                        <Option value="0">Não</Option>
                                    </Select>
                                    {hasStateRegistration && (
                                        <Controller
                                            name="hasStateRegistration"
                                            control={control}
                                            render={({ field }) => (
                                                <Input {...field} type="number" placeholder="Número" />
                                            )}
                                        />
                                    )}
                                </Space>
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item label="Possui registro civil de pessoas jurídicas?">
                                <Space direction="vertical" style={{ width: '100%' }}>
                                    <Select
                                        value={hasCivilRegistration ? "1" : "0"}
                                        onChange={(value) => {
                                            setHasCivilRegistration(value === '1');
                                            if (value === '0') {
                                                setValue("hasCivilRegistration", undefined);
                                            }
                                        }}
                                    >
                                        <Option value="1">Sim</Option>
                                        <Option value="0">Não</Option>
                                    </Select>
                                    {hasCivilRegistration && (
                                        <Controller
                                            name="hasCivilRegistration"
                                            control={control}
                                            render={({ field }) => (
                                                <Input {...field} type="number" placeholder="Número" />
                                            )}
                                        />
                                    )}
                                </Space>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row justify="end" className="mt-4">
                        <Col>
                            <Button type="primary" htmlType="submit">
                                <PlusOutlined /> Próxima Etapa
                            </Button>
                        </Col>
                    </Row>
                </div>
            </Form>
        </Spin>
    );
};
export default CompanyFinepTab;